/**
 * Registers the navigation CLIENT tools (navigate_to_page, open_guest_profile)
 * with the AI tool bridge while an event page is mounted. Pure UI — these drive
 * the user's own browser (route change / open the guest-detail popup) and touch
 * no data and no PAT, so they are safe app-wide. Renders nothing.
 *
 * Mirrors AiGuestlistTools: handlers are registered ONCE (stable identity for
 * the unregister race-guard) and read the latest context via refs.
 */
import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAiToolBridge } from "@/lib/ai/ai-tool-bridge";
import { useAiAvailabilityStore } from "@/lib/ai/ai-store";
import { useGuestDetailLauncher } from "@/components/PGuestDetails/GuestDetailLauncher";

/** kind → builder route under the event. email/document/wallet/calendar take a
 *  24-char template id; website takes a page slug. Keep in sync with App.tsx. */
const BUILDER_ROUTE: Record<string, (eventId: string, idOrSlug: string) => string> = {
  email: (e, id) => `/email-builder/${e}/${id}/configurator`,
  document: (e, id) => `/document-builder/${e}/${id}/configurator`,
  wallet: (e, id) => `/wallet-builder/${e}/${id}/configurator`,
  calendar: (e, id) => `/calendar-builder/${e}/${id}/configurator`,
  website: (e, slug) => `/website-builder/${e}/${slug}`,
};

/** pageName (schema enum) → path under /event/:eventId. Keep in sync with the
 *  get_event routes in App.tsx and the navigate_to_page enum in AiToolSchemas. */
const PAGE_PATHS: Record<string, string> = {
  dashboard: "dashboard",
  guestlist: "guests/guestlist",
  requests: "guests/requests",
  // W4/F7: slotting lives under Programme; keep the schema enum working.
  slotting: "programme?view=timeline",
  seating: "seating",
  website: "website",
  documents: "documents",
  attachments: "attachments",
  "automation-workflows": "automation/workflows",
  "automation-history": "automation/history",
  "check-in-devices": "check-in/devices",
  "check-in-history": "check-in/history",
  sender: "messaging/sender",
  "mail-templates": "messaging/mail-templates",
  "sent-mails": "messaging/sent-mails",
  "settings-general": "settings/general",
  "settings-rules": "settings/rules",
  "settings-plugins": "settings/plugins",
  "settings-notifications": "settings/notifications",
  "settings-access": "settings/access",
  programme: "programme",
  speakers: "speakers",
  sponsors: "sponsors",
  "settings-media": "settings/media",
};

export function AiNavigationTools() {
  const navigate = useNavigate();
  const { eventId = "" } = useParams<{ eventId: string }>();
  const { open } = useGuestDetailLauncher();

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const { register, unregister } = useAiToolBridge.getState();

    const navigateToPage = (rawInput: unknown) => {
      const input = (rawInput ?? {}) as { pageName?: string };
      const page = (input.pageName ?? "").trim();
      const path = PAGE_PATHS[page];
      if (!path) {
        return { ok: false, errorCode: "unknownPage", validPages: Object.keys(PAGE_PATHS) };
      }
      if (!eventIdRef.current) {
        return { ok: false, errorCode: "noEvent" };
      }
      navigateRef.current(`/event/${eventIdRef.current}/${path}`);
      return { ok: true, navigatedTo: page };
    };

    const openGuestProfile = (rawInput: unknown) => {
      const input = (rawInput ?? {}) as { guestId?: string };
      const guestId = (input.guestId ?? "").trim();
      if (!guestId) {
        return { ok: false, errorCode: "missingGuestId" };
      }
      openRef.current(guestId);
      return { ok: true, openedGuestId: guestId };
    };

    const openTemplateInBuilder = (rawInput: unknown) => {
      const input = (rawInput ?? {}) as { kind?: string; id?: string; slug?: string };
      const kind = (input.kind ?? "").trim();
      if (!BUILDER_ROUTE[kind]) {
        return { ok: false, errorCode: "unknownKind", validKinds: Object.keys(BUILDER_ROUTE) };
      }
      if (!eventIdRef.current) {
        return { ok: false, errorCode: "noEvent" };
      }
      // website opens by page slug; the others by 24-char template id.
      if (kind === "website") {
        const slug = (input.slug ?? "").trim();
        if (!slug) {
          // No slug → fall back to the website overview (NOT a builder, so the
          // auto-open flag must NOT be set or it would leak into a later open).
          navigateRef.current(`/event/${eventIdRef.current}/website`);
          return { ok: true, fallback: "website-overview" };
        }
        // Auto-open the in-builder AI panel on landing (one-shot flag).
        useAiAvailabilityStore.getState().setPendingBuilderAiOpen(true);
        navigateRef.current(BUILDER_ROUTE.website(eventIdRef.current, encodeURIComponent(slug)));
        return { ok: true, openedBuilder: kind };
      }
      const id = (input.id ?? "").trim();
      if (!/^[a-f0-9]{24}$/.test(id)) {
        return { ok: false, errorCode: "invalidTemplateId" };
      }
      // Auto-open the in-builder AI panel on landing (one-shot flag).
      useAiAvailabilityStore.getState().setPendingBuilderAiOpen(true);
      navigateRef.current(BUILDER_ROUTE[kind](eventIdRef.current, id));
      return { ok: true, openedBuilder: kind };
    };

    register("navigate_to_page", navigateToPage);
    register("open_guest_profile", openGuestProfile);
    register("open_template_in_builder", openTemplateInBuilder);
    return () => {
      unregister("navigate_to_page", navigateToPage);
      unregister("open_guest_profile", openGuestProfile);
      unregister("open_template_in_builder", openTemplateInBuilder);
    };
  }, []);

  return null;
}
